import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { useLocation, useNavigate } from "react-router-dom";
import Swal from "sweetalert2";
import "../../style/attendanceContent.css";
import { PageHeader, Card, EmptyState, SearchInput } from "../../components/admin/AdminUI";
import { SHOW_ZOOM_IMPORT, SHOW_RECOMPUTE } from "../../config/attendanceFeatures.js";

const CLASS_OPTIONS = ["OT", "NT", "BD", "BLC", "BCMF", "MASTERCLASS", "HH", "CBH", "CBD", "BWG", "MMM", "CONFERENCE"];

const swalError = (text) =>
  Swal.fire({ text, icon: "error", background: "white", color: "black", confirmButtonColor: "#0d9488" });
const swalSuccess = (text) =>
  Swal.fire({ text, icon: "success", background: "white", color: "black", confirmButtonColor: "#0d9488" });

const statusColor = (record) => {
  if (!record) return "#e0e0e0";
  if (record.needsReview) return "#f5c518";
  if (isMakeup(record)) return "#2563eb";
  return record.status === "present" ? "#2e9e6b" : "#e05252";
};

// Hadir lewat video kelas susulan — dihitung sama dengan hadir biasa, hanya
// centangnya berwarna biru supaya admin bisa membedakannya.
const isMakeup = (record) => record?.status === "present" && record?.source === "makeup";

const recordLabel = (record) => (isMakeup(record) ? "Hadir Susulan (video kelas susulan)" : record.status);

// `assistant` = halaman dibuka dari portal class assistant: fiturnya sama
// persis, yang berbeda hanya session mana yang dicek dan ke mana dialihkan
// kalau session-nya habis.
const AttendanceGridPage = ({ setLocationAdmin, assistant = false }) => {
  const navigate = useNavigate();
  const address = useLocation();
  const [errorMe, setErrorMe] = useState(false);

  const [classCode, setClassCode] = useState("OT");
  const [batch, setBatch] = useState("");
  // Class/batch di atas hanya isian form. Yang dipakai untuk fetch adalah
  // `applied`, yang baru berubah saat tombol Search diklik — supaya data tidak
  // ikut berganti tiap kali dropdown disentuh.
  const [applied, setApplied] = useState({ classCode: "", batch: "" });
  const [grid, setGrid] = useState(null);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(false);
  const [recomputing, setRecomputing] = useState(false);
  const [page, setPage] = useState(1);
  // `search` mengikuti ketikan admin; `appliedSearch` yang benar-benar dikirim
  // ke server, ditunda 350ms supaya tidak ada satu request per huruf.
  const [search, setSearch] = useState("");
  const [appliedSearch, setAppliedSearch] = useState("");

  // Email student yang barisnya sedang disimpan lewat tombol "All" — kotaknya
  // dinonaktifkan selama itu supaya klik ganda tidak mengirim dua request yang
  // saling menimpa.
  // "asc" | "desc" — dikirim ke server, bukan diurutkan di klien (lihat
  // komentar di getAttendanceGrid: daftarnya dipaginasi).
  const [sortDir, setSortDir] = useState("asc");
  // "name" | "studentId" — urutan Student ID dihitung di server (2 digit depan
  // lalu 4 digit belakang).
  const [sortBy, setSortBy] = useState("name");

  // Klik kolom yang sama membalik arah; pindah kolom mulai dari asc. Selalu
  // kembali ke halaman 1 karena urutan baru membuat isi tiap halaman berubah.
  const handleSort = (column) => {
    if (column === sortBy) {
      setSortDir((d) => (d === "asc" ? "desc" : "asc"));
    } else {
      setSortBy(column);
      setSortDir("asc");
    }
    setPage(1);
  };

  // Ikon sama dengan header tabel Student Data: panah aktif di kolom yang
  // sedang dipakai, panah dua arah samar di kolom lain.
  const sortIcon = (column) =>
    sortBy === column ? (
      <i className={`bi ${sortDir === "asc" ? "bi-arrow-up" : "bi-arrow-down"}`} style={{ marginLeft: "4px" }}></i>
    ) : (
      <i className="bi bi-arrow-down-up" style={{ marginLeft: "4px", opacity: 0.35 }}></i>
    );

  const [bulkSaving, setBulkSaving] = useState({});

  const [zoomClassCode, setZoomClassCode] = useState("OT");
  const [zoomBatch, setZoomBatch] = useState("");
  const [zoomFile, setZoomFile] = useState(null);
  const [importing, setImporting] = useState(false);

  const getMe = async () => {
    try {
      await axios.get(assistant ? "/assistant/me" : "/meAdmin");
    } catch (error) {
      setErrorMe(true);
    }
  };

  const fetchGrid = async () => {
    if (!applied.classCode || !applied.batch) return;
    setLoading(true);
    try {
      const res = await axios.get(`/attendance/${applied.classCode}/${applied.batch}`, {
        params: { page, sort: sortDir, sortBy, ...(appliedSearch ? { search: appliedSearch } : {}) },
      });
      setGrid(res.data);
    } catch (error) {
      setGrid(null);
    } finally {
      setLoading(false);
    }
  };

  // Menimpa satu record di dalam grid tanpa memuat ulang seluruh tabel.
  const patchRecordInGrid = (recordId, patch) => {
    setGrid((prev) =>
      prev
        ? {
            ...prev,
            students: prev.students.map((st) => ({
              ...st,
              records: st.records.map((r) => (r.id === recordId ? { ...r, ...patch } : r)),
            })),
          }
        : prev,
    );
  };

  // Menyisipkan record yang BARU dibuat ke student yang tepat, tanpa
  // memanggil fetchGrid() — fetchGrid menyalakan `loading` sehingga seluruh
  // tabel dicopot dan posisi scroll melompat setiap kali satu kotak
  // dicentang (alasan yang sama seperti pada patchRecordInGrid).
  const insertRecordInGrid = (studentEmail, record) => {
    setGrid((prev) =>
      prev
        ? {
            ...prev,
            students: prev.students.map((st) =>
              st.email === studentEmail ? { ...st, records: [...st.records, record] } : st,
            ),
          }
        : prev,
    );
  };

  const fetchSummary = async () => {
    if (!applied.classCode || !applied.batch) return;
    try {
      const res = await axios.get(`/attendance/${applied.classCode}/${applied.batch}/summary`);
      setSummary(res.data);
    } catch (error) {
      setSummary(null);
    }
  };

  useEffect(() => {
    setLocationAdmin(address);
    getMe();
  }, []);

  useEffect(() => {
    if (errorMe) {
      Swal.fire({
        title: "Session Expired",
        text: "Please login to your account !",
        icon: "warning",
        background: "white",
        color: "black",
        confirmButtonColor: "#fb3104",
        confirmButtonText: "Ok",
      });
      navigate(assistant ? "/assistant/login" : "/loginAdmin");
    }
  }, [errorMe, navigate, assistant]);

  // setAppliedSearch dan setPage(1) sengaja dilakukan bersamaan di satu tick:
  // React membatchnya jadi satu render, jadi efek fetch di bawah hanya jalan
  // sekali. Kalau page direset di efek terpisah, mengetik saat sedang berada di
  // halaman 3 akan menembak dua request — page 3 (kosong, layar sempat
  // menampilkan "tidak ada hasil") lalu page 1.
  useEffect(() => {
    const timer = setTimeout(() => {
      setAppliedSearch(search);
      setPage(1);
    }, 350);
    return () => clearTimeout(timer);
  }, [search]);

  useEffect(() => {
    fetchGrid();
    fetchSummary();
  }, [applied, page, appliedSearch, sortDir, sortBy]);

  // Tombol Search: satu-satunya cara memuat data untuk class/batch tertentu.
  const handleSearch = () => {
    if (!batch) return swalError("Enter a batch first.");
    setPage(1);
    setApplied({ classCode, batch });
  };

  const handleRecompute = async () => {
    if (!applied.batch) return swalError("Search a class and batch first.");
    setRecomputing(true);
    try {
      const res = await axios.post(`/attendance/${applied.classCode}/${applied.batch}/recompute`);
      swalSuccess(`Recomputed: ${res.data.created} created, ${res.data.updated} updated, ${res.data.needsReview} need review.`);
      fetchGrid();
      fetchSummary();
    } catch (error) {
      swalError(error.response?.data?.msg || "Recompute failed.");
    } finally {
      setRecomputing(false);
    }
  };

  const handleImport = async () => {
    if (!zoomFile || !zoomBatch) return swalError("Select a CSV file and enter a batch.");
    const formData = new FormData();
    formData.append("classCode", zoomClassCode);
    formData.append("batch", zoomBatch);
    formData.append("file", zoomFile);
    setImporting(true);
    try {
      const res = await axios.post("/attendance/zoom/import", formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      swalSuccess(`Imported ${res.data.imported} row(s): ${res.data.matched} matched, ${res.data.unmatched} unmatched.`);
    } catch (error) {
      swalError(error.response?.data?.msg || "Import failed.");
    } finally {
      setImporting(false);
    }
  };

  // Berbeda dari grid, endpoint summary mengembalikan SELURUH student tanpa
  // paginasi, jadi filternya cukup di sisi klien — tidak perlu request kedua.
  // Dicocokkan ke appliedSearch (bukan `search`) supaya kedua tabel berganti isi
  // pada saat yang sama, bukan summary duluan lalu grid menyusul.
  const filteredSummary = useMemo(() => {
    const rows = summary?.summary || [];
    const q = appliedSearch.trim().toLowerCase();
    if (!q) return rows;
    return rows.filter((r) => `${r.name || ""} ${r.email || ""}`.toLowerCase().includes(q));
  }, [summary, appliedSearch]);

  // Centang dibalik lokal lebih dulu lalu dikirim ke server. Sebelumnya baris ini
  // memanggil fetchGrid(), yang menyalakan `loading` sehingga seluruh tabel dicopot
  // dan diganti teks "Loading..." — halaman ikut menciut dan posisi scroll melompat
  // setiap kali satu kotak dicentang. Respons PATCH sudah membawa record final,
  // jadi cukup ditimpa di state.
  // Tombol "All": tandai SEMUA sesi kelas ini untuk satu student sekaligus.
  // Satu request untuk 10 sesi, bukan 10 request — selain jauh lebih cepat,
  // backend menjalankannya dalam satu transaksi sehingga tidak mungkin
  // separuh baris berubah dan separuh tidak.
  const toggleAllForStudent = async (student, makePresent) => {
    const sessionIds = (grid?.sessions || []).map((s) => s.id);
    if (sessionIds.length === 0) return;

    setBulkSaving((p) => ({ ...p, [student.email]: true }));
    try {
      const res = await axios.put("/attendance/records/bulk", {
        studentEmail: student.email,
        classSessionIds: sessionIds,
        status: makePresent ? "present" : "absent",
      });
      // Respons membawa seluruh record final, jadi baris itu cukup ditimpa di
      // state — tidak memanggil fetchGrid() yang akan mencopot tabel dan
      // melompatkan posisi scroll.
      const saved = res.data?.records || [];
      setGrid((prev) =>
        prev
          ? {
              ...prev,
              students: prev.students.map((st) => (st.email === student.email ? { ...st, records: saved } : st)),
            }
          : prev,
      );
      fetchSummary();
    } catch (error) {
      swalError(error.response?.data?.msg || "Failed to update all sessions.");
      // Kondisi sebenarnya di server tidak lagi bisa dipastikan dari sini,
      // jadi baris itu dimuat ulang dari sumbernya daripada menebak.
      fetchGrid();
    } finally {
      setBulkSaving((p) => {
        const next = { ...p };
        delete next[student.email];
        return next;
      });
    }
  };

  const toggleStatus = async (studentEmail, classSessionId, record) => {
    // Sesi yang belum punya record pun bisa dicentang: PUT /attendance/record
    // membuat-atau-mengubah baris berdasarkan (studentEmail, classSessionId),
    // jadi admin tidak perlu menjalankan recompute lebih dulu hanya untuk
    // mengisi absensi manual. Dulu jalur ini ditolak dengan pesan "No
    // attendance record exists for this session yet" dan admin kehabisan
    // jalan, karena baris attendance_record hanya lahir dari recompute.
    const next = record?.status === "present" ? "absent" : "present";

    // Optimistic update hanya mungkin kalau baris itu sudah punya id.
    const before = record ? { status: record.status, needsReview: record.needsReview, source: record.source } : null;
    if (record) patchRecordInGrid(record.id, { status: next, needsReview: false, source: "manual" });

    try {
      const res = await axios.put("/attendance/record", { studentEmail, classSessionId, status: next });
      const saved = res.data?.record;
      if (saved) {
        if (record) patchRecordInGrid(record.id, saved);
        else insertRecordInGrid(studentEmail, saved);
      }
      fetchSummary();
    } catch (error) {
      if (record) patchRecordInGrid(record.id, before);
      swalError(error.response?.data?.msg || "Failed to update.");
    }
  };

  return (
    <div className="adm-main">
      <PageHeader
        title="Attendance Grid"
        subtitle="Tick the sessions each student attended. Use “All” to mark every session for a student at once."
      />

      {SHOW_ZOOM_IMPORT && (
        <Card title="Import Zoom CSV">
          <div className="adm-form-grid adm-form-grid--4" style={{ alignItems: "end" }}>
            <div>
              <label className="form-label">Class</label>
              <select className="form-select" value={zoomClassCode} onChange={(e) => setZoomClassCode(e.target.value)}>
                {CLASS_OPTIONS.map((c) => (
                  <option key={c} value={c}>{c}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="form-label">Batch</label>
              <input className="form-control" value={zoomBatch} onChange={(e) => setZoomBatch(e.target.value.replace(/\D/g, ""))} placeholder="e.g. 4" />
            </div>
            <div>
              <label className="form-label">CSV File</label>
              <input type="file" accept=".csv" className="form-control" onChange={(e) => setZoomFile(e.target.files[0])} />
            </div>
            <div>
              <button type="button" className="adm-btn adm-btn--primary" disabled={importing} onClick={handleImport}>
                <i className="bi bi-upload"></i>
                {importing ? "Importing..." : "Import"}
              </button>
            </div>
          </div>
        </Card>
      )}

      <Card className={SHOW_ZOOM_IMPORT ? "adm-section" : ""}>
        <div className="adm-form-grid adm-form-grid--4" style={{ alignItems: "end" }}>
          <div>
            <label className="form-label">Class</label>
            <select className="form-select" value={classCode} onChange={(e) => setClassCode(e.target.value)}>
              {CLASS_OPTIONS.map((c) => (
                <option key={c} value={c}>{c}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="form-label">Batch</label>
            <input
              className="form-control"
              value={batch}
              onChange={(e) => setBatch(e.target.value.replace(/\D/g, ""))}
              onKeyDown={(e) => e.key === "Enter" && handleSearch()}
              placeholder="e.g. 4"
            />
          </div>
          <div className="d-flex gap-2">
            <button type="button" className="adm-btn adm-btn--primary" disabled={loading} onClick={handleSearch} style={{ height: "40px" }}>
              <i className="bi bi-search"></i>
              {loading ? "Loading..." : "Show Attendance"}
            </button>
            {SHOW_RECOMPUTE && (
              <button type="button" className="adm-btn adm-btn--secondary" disabled={recomputing} onClick={handleRecompute} style={{ height: "40px" }}>
                {recomputing ? "Recomputing..." : "Recompute"}
              </button>
            )}
          </div>
        </div>
      </Card>

      <div className="adm-section">
        {!applied.batch ? (
          <div className="adm-card">
            <EmptyState icon="bi-grid-3x3" title="Choose a class and batch" text="Then click Show Attendance to load the grid." />
          </div>
        ) : loading && !grid ? (
          <div className="adm-card">
            <EmptyState icon="bi-hourglass" title="Loading attendance…" />
          </div>
        ) : !grid || grid.sessions.length === 0 ? (
          <div className="adm-card">
            <EmptyState icon="bi-calendar-x" title="No sessions found" text="Generate sessions for this class/batch first on the Class Sessions page." />
          </div>
        ) : (
          // Kartu ini dirender selama sesinya ada, TERMASUK saat hasil pencarian
          // kosong — kalau kartunya ikut hilang, search bar di dalamnya hilang
          // juga dan admin tidak punya cara menghapus kata kuncinya.
          <Card
            title={`${applied.classCode} · Batch ${applied.batch}`}
            subtitle={`${grid.totalStudents} students · ${grid.sessions.length} sessions`}
            actions={<SearchInput value={search} onChange={setSearch} placeholder="Search name, email, or ID" />}
            flush
            className="adm-card--visible"
          >
            <div className="adm-grid-legend">
              <span><span className="adm-grid-dot" style={{ background: "#2e9e6b" }} /> Present</span>
              <span><span className="adm-grid-dot" style={{ background: "#2563eb" }} /> Present (catch-up video)</span>
              <span><span className="adm-grid-dot" style={{ background: "#f5c518" }} /> Needs review</span>
              <span><span className="adm-grid-dot" style={{ background: "#e05252" }} /> Absent</span>
              <span><span className="adm-grid-dot" style={{ background: "#d1d5db" }} /> No record yet</span>
            </div>
            {grid.students.length === 0 ? (
              // Dibedakan dari "no sessions": sesinya ada, yang kosong hasilnya.
              <EmptyState
                icon="bi-search"
                title="No students found"
                text={
                  appliedSearch
                    ? `No student matches "${appliedSearch}" in ${applied.classCode} batch ${applied.batch}.`
                    : "No students enrolled in this class/batch."
                }
              />
            ) : (
              <>
                {/* Hanya tabelnya yang boleh menggeser ke samping — kalau overflow
                    dipasang di kartu, search bar dan pagination ikut tergeser
                    keluar layar saat kolom sesinya banyak. */}
                <div className="attendance-sticky-table">
                  <table className="adm-table adm-table--compact">
                    <thead>
                      <tr>
                        {/* Ganti arah sort mengembalikan ke halaman 1: urutan baru
                            membuat "halaman 3" menunjuk sekumpulan student yang
                            berbeda, jadi tetap di halaman lama hanya membingungkan. */}
                        <th
                          onClick={() => handleSort("studentId")}
                          title={
                            sortBy !== "studentId"
                              ? "Klik untuk urut berdasarkan Student ID (2 digit depan, lalu 4 digit belakang)."
                              : sortDir === "asc"
                                ? "Urut ID kecil → besar. Klik untuk besar → kecil."
                                : "Urut ID besar → kecil. Klik untuk kecil → besar."
                          }
                          style={{ width: "110px", cursor: "pointer", userSelect: "none", whiteSpace: "nowrap" }}
                        >
                          Student ID {sortIcon("studentId")}
                        </th>
                        <th
                          onClick={() => handleSort("name")}
                          title={
                            sortBy !== "name"
                              ? "Klik untuk urut berdasarkan nama."
                              : sortDir === "asc"
                                ? "Urut A → Z. Klik untuk Z → A."
                                : "Urut Z → A. Klik untuk A → Z."
                          }
                          style={{ cursor: "pointer", userSelect: "none" }}
                        >
                          Student {sortIcon("name")}
                        </th>
                        <th style={{ textAlign: "center", width: "52px" }} title="Tandai semua sesi hadir / tidak hadir untuk student itu">
                          All
                        </th>
                        {grid.sessions.map((s) => (
                          <th key={s.id} style={{ textAlign: "center", width: "56px" }}>
                            D{s.dayNumber}S{s.sessionNumber}
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {grid.students.map((st) => (
                        <tr key={st.email}>
                          <td style={{ whiteSpace: "nowrap", color: "var(--adm-text-2)" }}>{st.studentId}</td>
                          <td style={{ fontWeight: 600 }}>{st.name}</td>
                          {(() => {
                            // Tercentang hanya kalau SEMUA sesi sudah present.
                            // Sebagian hadir ditampilkan sebagai indeterminate
                            // (garis, bukan centang) supaya tidak terbaca
                            // seolah-olah baris itu sudah penuh.
                            const total = grid.sessions.length;
                            const presentCount = grid.sessions.filter(
                              (s) => st.records.find((r) => r.classSessionId === s.id)?.status === "present",
                            ).length;
                            const allPresent = total > 0 && presentCount === total;
                            return (
                              <td style={{ textAlign: "center", width: "52px", background: "#fafafa" }}>
                                <input
                                  type="checkbox"
                                  checked={allPresent}
                                  disabled={!!bulkSaving[st.email]}
                                  ref={(el) => {
                                    if (el) el.indeterminate = presentCount > 0 && !allPresent;
                                  }}
                                  onChange={() => toggleAllForStudent(st, !allPresent)}
                                  title={
                                    allPresent
                                      ? "Hadir di semua sesi — klik untuk mengosongkan semua"
                                      : `${presentCount}/${total} hadir — klik untuk menandai hadir semua`
                                  }
                                  style={{
                                    width: "18px",
                                    height: "18px",
                                    margin: 0,
                                    verticalAlign: "middle",
                                    accentColor: "var(--adm-primary)",
                                    cursor: bulkSaving[st.email] ? "wait" : "pointer",
                                  }}
                                />
                              </td>
                            );
                          })()}
                          {grid.sessions.map((s) => {
                            const record = st.records.find((r) => r.classSessionId === s.id);
                            return (
                              <td key={s.id} style={{ textAlign: "center", width: "56px" }}>
                                <input
                                  type="checkbox"
                                  checked={record?.status === "present"}
                                  disabled={!!bulkSaving[st.email]}
                                  onChange={() => toggleStatus(st.email, s.id, record)}
                                  title={
                                    record
                                      ? `${recordLabel(record)}${record.needsReview ? " (needs review)" : ""}`
                                      : "belum ada record — klik untuk menandai hadir"
                                  }
                                  style={{
                                    width: "18px",
                                    height: "18px",
                                    margin: 0,
                                    verticalAlign: "middle",
                                    accentColor: statusColor(record),
                                    cursor: "pointer",
                                  }}
                                />
                              </td>
                            );
                          })}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                <div className="adm-table-footer">
                  <span>
                    Page {grid.page} of {grid.totalPages || 1} · {grid.totalStudents} students
                  </span>
                  <div className="d-flex gap-2">
                    <button type="button" className="adm-btn adm-btn--secondary adm-btn--sm" disabled={page <= 1} onClick={() => setPage((p) => p - 1)}>
                      <i className="bi bi-chevron-left"></i> Prev
                    </button>
                    <button
                      type="button"
                      className="adm-btn adm-btn--secondary adm-btn--sm"
                      disabled={page >= (grid.totalPages || 1)}
                      onClick={() => setPage((p) => p + 1)}
                    >
                      Next <i className="bi bi-chevron-right"></i>
                    </button>
                  </div>
                </div>
              </>
            )}
          </Card>
        )}
      </div>

      {summary && summary.summary.length > 0 && filteredSummary.length > 0 && (
        <Card title="Summary" flush className="adm-section adm-card--visible">
          <div className="attendance-sticky-table">
            <table className="adm-table adm-table--compact">
              <thead>
                <tr>
                  <th>Student</th>
                  <th>JS</th>
                  <th>TH</th>
                  <th>PT%</th>
                  <th>Score</th>
                  <th>AG</th>
                  <th>Status</th>
                </tr>
              </thead>
              <tbody>
                {filteredSummary.map((s) => (
                  <tr key={s.email}>
                    <td style={{ fontWeight: 600 }}>{s.name}</td>
                    <td>{s.js}</td>
                    <td>{s.th}</td>
                    <td>{s.pt}%</td>
                    <td>{s.score}</td>
                    <td>{s.ag}</td>
                    <td>
                      <span className={`adm-status ${s.status === "PASSED" ? "adm-status--success" : "adm-status--danger"}`}>{s.status}</span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>
      )}
    </div>
  );
};

export default AttendanceGridPage;
